import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, Plus, X, Upload } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { categoryLabel, formatPrice } from "../components/ProductCard";

const EMPTY_PRODUCT = {
  name: "", price: "", category: "meubles", description: "", image: "",
  stock: 10, origin: "", material: "", featured: false, article_slug: "",
};
const EMPTY_ARTICLE = {
  title: "", excerpt: "", content: "", cover_image: "",
  category: "Récit", author: "Maison Afra", published: true,
};
const CATEGORIES = ["meubles", "decoration", "bijoux", "artisanat", "autres"];
const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [articles, setArticles] = useState([]);
  const [vip, setVip] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [contentSaving, setContentSaving] = useState({});
  const [contentSaved, setContentSaved] = useState({});
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [articleForm, setArticleForm] = useState(EMPTY_ARTICLE);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const articleFileRef = useRef(null);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    else if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  const load = async () => {
    const [p, o, a, v, c, m] = await Promise.all([
      api.get("/products"),
      api.get("/admin/orders").catch(() => ({ data: [] })),
      api.get("/articles"),
      api.get("/admin/vip").catch(() => ({ data: [] })),
      api.get("/content").catch(() => ({ data: [] })),
      api.get("/settings/maintenance").catch(() => ({ data: { enabled: false } })),
    ]);
    setProducts(p.data); setOrders(o.data); setArticles(a.data); setVip(v.data); setContentItems(c.data);
    setMaintenance(!!m.data.enabled);
  };

  const toggleMaintenance = async () => {
    setMaintenanceSaving(true);
    try {
      const { data } = await api.put("/admin/settings/maintenance", { enabled: !maintenance });
      setMaintenance(!!data.enabled);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setMaintenanceSaving(false); }
  };

  useEffect(() => {
    if (user && user !== false && user.role === "admin") load();
  }, [user]);

  // ---- Products ----
  const openCreate = () => { setForm(EMPTY_PRODUCT); setEditing({}); setError(""); };
  const openEdit = (p) => {
    setForm({
      name: p.name, price: p.price, category: p.category, description: p.description,
      image: p.image, stock: p.stock, origin: p.origin || "", material: p.material || "", featured: !!p.featured,
      article_slug: p.article_slug || "",
    });
    setEditing(p); setError("");
  };
  const closeModal = () => setEditing(null);

  const handleUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (target === "product") setForm((f) => ({ ...f, image: res.url }));
      else setArticleForm((f) => ({ ...f, cover_image: res.url }));
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock, 10) || 0,
        article_slug: form.article_slug || null };
      if (editing && editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      await load();
      closeModal();
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    await api.delete(`/admin/products/${p.id}`); await load();
  };

  // ---- Articles ----
  const openCreateArticle = () => { setArticleForm(EMPTY_ARTICLE); setEditingArticle({}); setError(""); };
  const openEditArticle = (a) => {
    setArticleForm({
      title: a.title, excerpt: a.excerpt, content: a.content, cover_image: a.cover_image,
      category: a.category || "Récit", author: a.author || "Maison Afra", published: a.published !== false,
    });
    setEditingArticle(a); setError("");
  };
  const saveArticle = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...articleForm };
      if (editingArticle && editingArticle.id) await api.put(`/admin/articles/${editingArticle.id}`, payload);
      else await api.post("/admin/articles", payload);
      await load();
      setEditingArticle(null);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setSaving(false); }
  };
  const removeArticle = async (a) => {
    if (!window.confirm(`Supprimer « ${a.title} » ?`)) return;
    await api.delete(`/admin/articles/${a.id}`); await load();
  };

  // ---- Orders ----
  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status });
      await load();
    } catch (e2) {
      alert(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const sendStory = async (orderId) => {
    if (!window.confirm("Envoyer le récit éditorial à ce client ?")) return;
    try {
      await api.post(`/admin/orders/${orderId}/send-story`);
      alert("Récit envoyé.");
      await load();
    } catch (e2) {
      alert(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  // ---- Content ----
  const updateContentField = (key, field, value) => {
    setContentItems((items) => items.map((it) => it.key === key ? { ...it, [field]: value } : it));
    setContentSaved((s) => ({ ...s, [key]: false }));
  };
  const saveContent = async (item) => {
    setContentSaving((s) => ({ ...s, [item.key]: true }));
    try {
      const payload = item.type === "image"
        ? { url: item.url, fr: "", en: "" }
        : { fr: item.fr, en: item.en, url: "" };
      await api.put(`/admin/content/${item.key}`, payload);
      setContentSaved((s) => ({ ...s, [item.key]: true }));
      setTimeout(() => setContentSaved((s) => ({ ...s, [item.key]: false })), 2500);
    } catch (e2) {
      alert(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setContentSaving((s) => ({ ...s, [item.key]: false }));
    }
  };
  const uploadContentImage = async (key, file) => {
    if (!file) return;
    setContentSaving((s) => ({ ...s, [key]: true }));
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      updateContentField(key, "url", res.url);
    } catch (e2) {
      alert(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setContentSaving((s) => ({ ...s, [key]: false }));
    }
  };

  if (!user || user === false) {
    return <div className="pt-40 pb-24 max-w-[1500px] mx-auto px-6 md:px-12 text-[#A3A39D]">Vérification…</div>;
  }

  return (
    <div data-testid="admin-dashboard" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-5">Tableau de bord</div>
            <h1 className="font-serif text-5xl md:text-6xl text-[#F5F5F0] leading-[0.95]">Administration</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleMaintenance}
              disabled={maintenanceSaving}
              data-testid="maintenance-toggle-btn"
              className={`inline-flex items-center gap-3 px-4 py-2 border text-xs uppercase tracking-widest transition-colors ${
                maintenance
                  ? "border-red-500/60 text-red-400 bg-red-500/10"
                  : "border-white/20 text-[#A3A39D] hover:border-[#D4AF37]/50"
              }`}
            >
              <span
                className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                  maintenance ? "bg-red-500/70" : "bg-white/15"
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#F5F5F0] transition-all ${
                    maintenance ? "left-[18px]" : "left-0.5"
                  }`}
                />
              </span>
              {maintenance ? "Maintenance activée" : "Mode maintenance"}
            </button>
            {tab === "products" && (
              <button onClick={openCreate} data-testid="admin-create-btn" className="btn-primary inline-flex items-center gap-2">
                <Plus size={14} strokeWidth={1.5} /> Nouveau produit
              </button>
            )}
            {tab === "articles" && (
              <button onClick={openCreateArticle} data-testid="admin-create-article-btn" className="btn-primary inline-flex items-center gap-2">
                <Plus size={14} strokeWidth={1.5} /> Nouvel article
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-8 border-b border-white/10 mb-10 overflow-x-auto">
          <Tab id="products" tab={tab} setTab={setTab}>Produits ({products.length})</Tab>
          <Tab id="orders" tab={tab} setTab={setTab}>Commandes ({orders.length})</Tab>
          <Tab id="articles" tab={tab} setTab={setTab}>La Revue ({articles.length})</Tab>
          <Tab id="vip" tab={tab} setTab={setTab}>Cercle VIP ({vip.length})</Tab>
          <Tab id="content" tab={tab} setTab={setTab}>Contenus ({contentItems.length})</Tab>
        </div>

        {/* PRODUCTS */}
        {tab === "products" && (
          <div className="grid grid-cols-1 gap-3">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-row-${p.id}`} className="flex items-center gap-5 bg-[#151516] border border-white/10 p-4">
                <div className="w-16 h-20 bg-[#0a0a0b] shrink-0">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-xl text-[#F5F5F0]">{p.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#A3A39D] mt-1">
                    {categoryLabel(p.category)} · Stock {p.stock}{p.featured ? " · Signature" : ""}
                  </div>
                </div>
                <div className="text-[#D4AF37] font-serif text-xl hidden md:block">{formatPrice(p.price)}</div>
                <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="p-2 text-[#A3A39D] hover:text-[#D4AF37]" aria-label="Modifier">
                  <Pencil size={16} strokeWidth={1.25} />
                </button>
                <button onClick={() => remove(p)} data-testid={`admin-delete-${p.id}`} className="p-2 text-[#A3A39D] hover:text-red-400" aria-label="Supprimer">
                  <Trash2 size={16} strokeWidth={1.25} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ORDERS */}
        {tab === "orders" && (
          <div className="grid grid-cols-1 gap-3">
            {orders.length === 0 ? (
              <div className="text-[#A3A39D] text-sm">Aucune commande pour le moment.</div>
            ) : orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-[#151516] border border-white/10 p-5">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-3">
                  <div>
                    <div className="font-serif text-xl text-[#F5F5F0]">{o.full_name}</div>
                    <div className="text-xs text-[#A3A39D] mt-1">
                      {o.email} · {o.phone} · {o.city}, {o.country}
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row gap-3 md:items-center">
                    <select
                      value={o.status || "pending"}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      data-testid={`status-select-${o.id}`}
                      className="bg-[#0a0a0b] border border-white/20 text-[#F5F5F0] text-xs uppercase tracking-[0.18em] px-3 py-2 outline-none focus:border-[#D4AF37]"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#0a0a0b]">{s}</option>
                      ))}
                    </select>
                    <div className="text-right">
                      <div className="text-[#D4AF37] font-serif text-xl">{formatPrice(o.total)}</div>
                      <div className="overline text-[10px] mt-1">#{o.id.slice(0, 8)}</div>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-[#A3A39D]">
                  {o.items.map((i) => `${i.name} × ${i.quantity}`).join(" · ")}
                </div>
                {o.notes && <div className="text-xs text-[#A3A39D] mt-2 italic">{o.notes}</div>}
                <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-white/10">
                  <button
                    onClick={() => sendStory(o.id)}
                    data-testid={`send-story-${o.id}`}
                    disabled={o.story_email_sent}
                    className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {o.story_email_sent ? "Récit envoyé ✓" : "Envoyer le récit"}
                  </button>
                  {o.story_email_sent_at && (
                    <span className="text-[10px] text-[#A3A39D] uppercase tracking-widest">
                      {new Date(o.story_email_sent_at).toLocaleString("fr-FR")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ARTICLES */}
        {tab === "articles" && (
          <div className="grid grid-cols-1 gap-3">
            {articles.length === 0 ? (
              <div className="text-[#A3A39D] text-sm">Aucun article publié.</div>
            ) : articles.map((a) => (
              <div key={a.id} data-testid={`admin-article-${a.id}`} className="flex items-center gap-5 bg-[#151516] border border-white/10 p-4">
                <div className="w-20 h-16 bg-[#0a0a0b] shrink-0">
                  <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-lg text-[#F5F5F0]">{a.title}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#A3A39D] mt-1">
                    {a.category} · {a.author} · /{a.slug}
                  </div>
                </div>
                <button onClick={() => openEditArticle(a)} data-testid={`admin-edit-article-${a.id}`} className="p-2 text-[#A3A39D] hover:text-[#D4AF37]"><Pencil size={16} strokeWidth={1.25} /></button>
                <button onClick={() => removeArticle(a)} data-testid={`admin-delete-article-${a.id}`} className="p-2 text-[#A3A39D] hover:text-red-400"><Trash2 size={16} strokeWidth={1.25} /></button>
              </div>
            ))}
          </div>
        )}

        {/* VIP */}
        {tab === "vip" && (
          <div className="grid grid-cols-1 gap-3">
            {vip.length === 0 ? (
              <div className="text-[#A3A39D] text-sm">Aucun inscrit pour le moment.</div>
            ) : vip.map((v) => (
              <div key={v.id} className="flex items-center justify-between bg-[#151516] border border-white/10 p-4">
                <div>
                  <div className="text-[#F5F5F0]">{v.name || "—"}</div>
                  <div className="text-xs text-[#A3A39D] mt-1">{v.email}</div>
                </div>
                <div className="overline text-[10px]">{new Date(v.created_at).toLocaleDateString("fr-FR")}</div>
              </div>
            ))}
          </div>
        )}

        {/* CONTENT */}
        {tab === "content" && (
          <div className="grid grid-cols-1 gap-4" data-testid="content-editor">
            <p className="text-sm text-[#A3A39D] mb-2 max-w-3xl">
              Ces blocs alimentent les pages publiques du site (accueil, manifeste, contact, VIP, footer).
              Modifiez textes et images, puis enregistrez chaque bloc.
            </p>
            {contentItems.map((item) => (
              <div key={item.key} data-testid={`content-${item.key}`} className="bg-[#151516] border border-white/10 p-5">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div>
                    <div className="font-serif text-lg text-[#F5F5F0]">{item.label}</div>
                    <div className="overline text-[10px] mt-1">{item.type === "image" ? "Image" : "Texte"} · {item.key}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {contentSaved[item.key] && (
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">✓ Enregistré</span>
                    )}
                    <button
                      onClick={() => saveContent(item)}
                      disabled={!!contentSaving[item.key]}
                      data-testid={`save-${item.key}`}
                      className="btn-primary text-xs disabled:opacity-60"
                    >
                      {contentSaving[item.key] ? "…" : "Enregistrer"}
                    </button>
                  </div>
                </div>
                {item.type === "image" ? (
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-5 items-start">
                    <div className="space-y-3">
                      <label className="lux-label">URL de l'image</label>
                      <input
                        value={item.url || ""}
                        onChange={(e) => updateContentField(item.key, "url", e.target.value)}
                        className="lux-input"
                        data-testid={`field-url-${item.key}`}
                      />
                      <label className="btn-ghost inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => { uploadContentImage(item.key, e.target.files?.[0]); e.target.value = ""; }}
                          className="hidden"
                          data-testid={`upload-${item.key}`}
                        />
                        {contentSaving[item.key] ? "Téléversement…" : "Téléverser une image"}
                      </label>
                    </div>
                    {item.url && (
                      <div className="w-full aspect-[4/3] bg-[#0a0a0b] border border-white/10">
                        <img src={item.url} alt={item.label} className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="lux-label">Français</label>
                      <textarea
                        value={item.fr}
                        onChange={(e) => updateContentField(item.key, "fr", e.target.value)}
                        className="lux-input min-h-[90px]"
                        data-testid={`field-fr-${item.key}`}
                      />
                    </div>
                    <div>
                      <label className="lux-label">English</label>
                      <textarea
                        value={item.en}
                        onChange={(e) => updateContentField(item.key, "en", e.target.value)}
                        className="lux-input min-h-[90px]"
                        data-testid={`field-en-${item.key}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PRODUCT MODAL */}
      {editing !== null && (
        <Modal title={editing.id ? "Modifier le produit" : "Nouveau produit"} onClose={closeModal}>
          <form onSubmit={save} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-product-form">
            <div className="md:col-span-2">
              <label className="lux-label">Nom</label>
              <input className="lux-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-field-name" />
            </div>
            <div>
              <label className="lux-label">Prix (€)</label>
              <input type="number" step="0.01" className="lux-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="admin-field-price" />
            </div>
            <div>
              <label className="lux-label">Catégorie</label>
              <select className="lux-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-field-category">
                {CATEGORIES.map((c) => (<option key={c} value={c} className="bg-[#151516]">{categoryLabel(c)}</option>))}
              </select>
            </div>
            <div>
              <label className="lux-label">Stock</label>
              <input type="number" className="lux-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="admin-field-stock" />
            </div>
            <div>
              <label className="lux-label">Mise en avant</label>
              <label className="flex items-center gap-3 mt-3">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-field-featured" />
                <span className="text-sm text-[#A3A39D]">Pièce signature</span>
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="lux-label">Image principale</label>
              <div className="flex flex-col md:flex-row gap-3">
                <input className="lux-input flex-1" placeholder="URL ou téléverser ci-dessous"
                  value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} required data-testid="admin-field-image" />
                <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleUpload(e, "product")} className="hidden" data-testid="admin-image-input" />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-upload-image-btn">
                  <Upload size={14} strokeWidth={1.25} /> {uploading ? "Téléversement…" : "Téléverser"}
                </button>
              </div>
              {form.image && (
                <div className="mt-3 w-32 h-40 bg-[#0a0a0b]">
                  <img src={form.image} alt="aperçu" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div>
              <label className="lux-label">Origine</label>
              <input className="lux-input" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="admin-field-origin" />
            </div>
            <div>
              <label className="lux-label">Matériaux</label>
              <input className="lux-input" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="admin-field-material" />
            </div>
            <div className="md:col-span-2">
              <label className="lux-label">Description</label>
              <textarea className="lux-input min-h-[120px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="admin-field-description" />
            </div>

            <div className="md:col-span-2">
              <label className="lux-label">Récit éditorial associé (envoyé 48h après la commande)</label>
              <select
                className="lux-input"
                value={form.article_slug || ""}
                onChange={(e) => setForm({ ...form, article_slug: e.target.value })}
                data-testid="admin-field-article-slug"
              >
                <option value="" className="bg-[#151516]">— Auto (selon la catégorie) —</option>
                {articles.map((a) => (
                  <option key={a.slug} value={a.slug} className="bg-[#151516]">
                    {a.title}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-[#A3A39D] tracking-widest uppercase mt-2">
                Laissez vide pour utiliser le mapping automatique (meubles → Abidjan, bijoux → Ashanti, artisanat → Baoulé).
              </p>
            </div>

            {error && <div data-testid="admin-form-error" className="md:col-span-2 text-sm text-red-400 border border-red-900/60 bg-red-900/10 p-3">{error}</div>}

            <div className="md:col-span-2 flex gap-3">
              <button type="submit" disabled={saving} data-testid="admin-save-btn" className="btn-primary">
                {saving ? "Enregistrement…" : editing.id ? "Enregistrer" : "Créer le produit"}
              </button>
              <button type="button" onClick={closeModal} className="btn-ghost">Annuler</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ARTICLE MODAL */}
      {editingArticle !== null && (
        <Modal title={editingArticle.id ? "Modifier l'article" : "Nouvel article"} onClose={() => setEditingArticle(null)}>
          <form onSubmit={saveArticle} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-article-form">
            <div className="md:col-span-2">
              <label className="lux-label">Titre</label>
              <input className="lux-input" value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })} required data-testid="admin-article-title" />
            </div>
            <div>
              <label className="lux-label">Auteur</label>
              <input className="lux-input" value={articleForm.author} onChange={(e) => setArticleForm({ ...articleForm, author: e.target.value })} />
            </div>
            <div>
              <label className="lux-label">Catégorie</label>
              <input className="lux-input" value={articleForm.category} onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })} />
            </div>

            <div className="md:col-span-2">
              <label className="lux-label">Image de couverture</label>
              <div className="flex flex-col md:flex-row gap-3">
                <input className="lux-input flex-1" value={articleForm.cover_image}
                  onChange={(e) => setArticleForm({ ...articleForm, cover_image: e.target.value })} required data-testid="admin-article-cover" />
                <input ref={articleFileRef} type="file" accept="image/*" onChange={(e) => handleUpload(e, "article")} className="hidden" />
                <button type="button" onClick={() => articleFileRef.current?.click()} disabled={uploading}
                  className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50">
                  <Upload size={14} strokeWidth={1.25} /> {uploading ? "Téléversement…" : "Téléverser"}
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="lux-label">Chapô (extrait)</label>
              <textarea className="lux-input min-h-[80px]" value={articleForm.excerpt}
                onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })} required data-testid="admin-article-excerpt" />
            </div>
            <div className="md:col-span-2">
              <label className="lux-label">Contenu (HTML autorisé)</label>
              <textarea className="lux-input min-h-[280px] font-mono text-xs" value={articleForm.content}
                onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })} required data-testid="admin-article-content" />
            </div>

            {error && <div className="md:col-span-2 text-sm text-red-400 border border-red-900/60 bg-red-900/10 p-3">{error}</div>}

            <div className="md:col-span-2 flex gap-3">
              <button type="submit" disabled={saving} data-testid="admin-article-save" className="btn-primary">
                {saving ? "Enregistrement…" : editingArticle.id ? "Enregistrer" : "Publier l'article"}
              </button>
              <button type="button" onClick={() => setEditingArticle(null)} className="btn-ghost">Annuler</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Tab({ id, tab, setTab, children }) {
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`tab-${id}`}
      className={`pb-4 text-xs uppercase tracking-[0.2em] whitespace-nowrap ${
        tab === id ? "text-[#D4AF37] border-b border-[#D4AF37]" : "text-[#A3A39D]"
      }`}
    >
      {children}
    </button>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#151516] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="font-serif text-2xl text-[#F5F5F0]">{title}</h2>
          <button onClick={onClose} data-testid="admin-modal-close" className="text-[#A3A39D] hover:text-[#D4AF37]"><X strokeWidth={1.25} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
